import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import type { IApiError } from '../models/api.model';

/**
 * Parses an error response body into IApiError shape.
 * Falls back to a generic error if parsing fails.
 */
function parseApiError(error: unknown): IApiError {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error
  ) {
    // Already IApiError shape
    return error as IApiError;
  }
  if (
    typeof error === 'string'
  ) {
    return {
      code: 'INTERNAL_ERROR',
      message: error,
    };
  }
  // Fallback
  return {
    code: 'INTERNAL_ERROR',
    message: 'An unexpected error occurred.',
  };
}

/**
 * Error handling HTTP interceptor.
 * Handles API error responses and maps status codes to user actions.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const notificationService = inject(NotificationService);

  return next(req).pipe(
    catchError((err: unknown) => {
      // Only handle HttpErrorResponse
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      // Try to parse API error shape from response body
      let apiError: IApiError = {
        code: 'INTERNAL_ERROR',
        message: 'An unexpected error occurred.',
      };
      if (err.error) {
        try {
          if (typeof err.error === 'string') {
            // Try to parse JSON string
            apiError = parseApiError(JSON.parse(err.error));
          } else {
            apiError = parseApiError(err.error);
          }
        } catch {
          // Ignore parsing errors, use fallback
        }
      }

      switch (err.status) {
        case 401:
          // Unauthorized: clear token and redirect to login
          authService.clearToken();
          router.navigateByUrl('/login');
          break;

        case 403:
          // Forbidden: show access denied snackbar
          notificationService.showError(
            apiError.message || 'Access denied.'
          );
          break;

        case 404:
          // Not found: let component handle (do not show global error)
          break;

        case 409:
          // Conflict: show conflict message
          notificationService.showError(
            apiError.message || 'A conflict occurred. Please check your input.'
          );
          break;

        case 500:
          // Internal server error: show generic error
          notificationService.showError(
            apiError.message || 'A server error occurred. Please try again later.'
          );
          break;

        default:
          // Other errors: show generic error
          if (err.status !== 404) {
            notificationService.showError(
              apiError.message || 'An unexpected error occurred.'
            );
          }
          break;
      }

      // Always propagate the parsed error for component-level handling
      return throwError(() => apiError);
    })
  );
};